// =============================================================================
// src/lib/inboundSmsApi.js - server calls for the Inbound SMS cockpit
// (migration 20261019100000_inbound_sms_cockpit). Every function throws on
// error; the error's message is the server's short code (map it with
// inboundErrorText in inboundSmsCore.js).
//
//   fetchInbox(view)                 inbound_sms_inbox
//   fetchCounts()                    inbound_sms_counts (also the nav badge)
//   fetchThread(id)                  inbound_sms_thread
//   fetchStaff()                     inbound_sms_staff
//   claimConversation(id)            inbound_sms_claim
//   assignConversations(ids, uid)    inbound_sms_assign (null = unassign) -> { assigned, failed }
//   closeConversations(ids, reason, note) -> { closed, skipped_crisis, failed }
//   reopenConversation(id)
//   ackCrisis(id, action, note)
//   linkNumberToPatient(id, patientId, overrideReason)
//   saveDraftEdit(draftId, body) / discardDraft(draftId)
//   generateDraft(inboundId)         pro-inbound-sms-draft { inboundId }
//   sendReply({...})                 inbound-sms-reply edge function
//   fetchSettings() / saveSettings(patch)
//   fetchInsights(from, to)
//   searchPatients(text)             patients (RLS), for linking a number
//   After-hours triage (Oct 2026, migration 20261212100000_after_hours_triage):
//   fetchOnCall() / saveOnCallContact / removeOnCallContact / saveOnCallShift /
//   removeOnCallShift / saveOnCallDefaults   inbound_sms_oncall_*
//   ackPage(pageId)                  inbound_sms_page_ack
//   makeQueueTask(id, role)          inbound_sms_queue_make_task
//   fetchOvernightSummary()          inbound_sms_overnight_summary (no PHI)
// =============================================================================
import { supabase } from "./supabaseClient";

function codeError(error, fallback) {
  const e = new Error((error && error.message) || fallback || "request_failed");
  e.code = e.message;
  return e;
}

async function rpc(name, args) {
  const { data, error } = await supabase.rpc(name, args || {});
  if (error) throw codeError(error, name + "_failed");
  return data;
}

// supabase.functions.invoke hides the server's JSON on non-2xx; read it back.
async function invoke(name, body) {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) {
    let payload = null;
    try {
      if (error.context && typeof error.context.text === "function") {
        const text = await error.context.text();
        payload = text ? JSON.parse(text) : null;
      }
    } catch (_e) {
      payload = null;
    }
    const e = new Error((payload && (payload.error || payload.detail)) || error.message || "request_failed");
    e.code = e.message;
    e.payload = payload;
    e.status = (error.context && error.context.status) || 500;
    throw e;
  }
  if (data && data.ok === false) throw codeError({ message: data.error }, "request_failed");
  return data;
}

const asArray = (v) => (Array.isArray(v) ? v : []);

export async function fetchInbox(view = "open") {
  return asArray(await rpc("inbound_sms_inbox", { p_view: view, p_limit: 200 }));
}

export async function fetchCounts() {
  const d = await rpc("inbound_sms_counts");
  return d && !Array.isArray(d) ? d : {};
}

export async function fetchThread(conversationId) {
  const d = await rpc("inbound_sms_thread", { p_conversation_id: conversationId });
  if (!d || Array.isArray(d)) throw codeError({ message: "conversation_not_found" });
  return { ...d, items: asArray(d.items), drafts: asArray(d.drafts), copilot_tasks: asArray(d.copilot_tasks) };
}

export async function fetchStaff() {
  return asArray(await rpc("inbound_sms_staff"));
}

export const claimConversation = (id) => rpc("inbound_sms_claim", { p_conversation_id: id });
export const assignConversations = (ids, userId) => rpc("inbound_sms_assign", { p_ids: ids, p_user_id: userId || null });
export const closeConversations = (ids, reason, note) =>
  rpc("inbound_sms_close", { p_ids: ids, p_reason_code: reason, p_note: note || null });
export const reopenConversation = (id) => rpc("inbound_sms_reopen", { p_conversation_id: id });
export const ackCrisis = (id, action, note) =>
  rpc("inbound_sms_ack_crisis", { p_conversation_id: id, p_action: action, p_note: note || null });
// The patient's mobile must match the number; leads may override with a reason.
export const linkNumberToPatient = (id, patientId, overrideReason) =>
  rpc("inbound_sms_link_patient", { p_conversation_id: id, p_patient_id: patientId, p_override_reason: overrideReason || null });
export const saveDraftEdit = (draftId, body) => rpc("inbound_sms_save_draft", { p_draft_id: draftId, p_body: body });
export const discardDraft = (draftId) => rpc("inbound_sms_discard_draft", { p_draft_id: draftId });

export async function generateDraft(inboundId) {
  const d = await invoke("pro-inbound-sms-draft", { inboundId });
  if (d && d.skipped) throw codeError({ message: d.error || d.reason });
  return d;
}

// kind: staff | quick_reply | ai_draft | crisis_template. clientKey makes a
// retry of the same attempt safe (the server never sends it twice).
export function sendReply({ conversationId, body, kind, clientKey, draftId, replyToInboundId }) {
  return invoke("inbound-sms-reply", {
    conversation_id: conversationId,
    body,
    kind: kind || "staff",
    client_key: clientKey,
    draft_id: draftId || null,
    reply_to_inbound_id: replyToInboundId || null,
  });
}

export async function fetchSettings() {
  const d = await rpc("inbound_sms_get_settings");
  return d && !Array.isArray(d) ? d : {};
}

export const saveSettings = (patch) => rpc("inbound_sms_save_settings", { p: patch });

export async function fetchInsights(from, to) {
  const d = await rpc("inbound_sms_insights", { p_from: from, p_to: to });
  return d && !Array.isArray(d) ? d : {};
}

export async function searchPatients(text) {
  const q = String(text || "").trim().replace(/[%_,()*.]/g, " ").replace(/\s+/g, "%");
  if (q.length < 2) return [];
  const like = "%" + q + "%";
  const { data, error } = await supabase
    .from("patients")
    .select("id, first_name, last_name, mrn, date_of_birth, phone_mobile")
    .or("first_name.ilike." + like + ",last_name.ilike." + like + ",mrn.ilike." + like + ",phone_mobile.ilike." + like)
    .order("last_name", { ascending: true })
    .limit(8);
  if (error) throw codeError(error, "patient_search_failed");
  return asArray(data);
}

// ---- After-hours triage (Oct 2026) ----------------------------------------------------
const asObject = (d) => (d && !Array.isArray(d) ? d : {});

export async function fetchOnCall() {
  return asObject(await rpc("inbound_sms_oncall_get"));
}
// userId null = yourself. consent must be true.
export async function saveOnCallContact(userId, mobile, consent) {
  return asObject(await rpc("inbound_sms_oncall_save_contact", { p_user_id: userId || null, p_mobile: mobile, p_consent: consent === true }));
}
export async function removeOnCallContact(userId) {
  return asObject(await rpc("inbound_sms_oncall_remove_contact", { p_user_id: userId || null }));
}
export async function saveOnCallShift({ id, startsAt, endsAt, primary, backup }) {
  return asObject(await rpc("inbound_sms_oncall_save_shift", {
    p_id: id || null, p_starts_at: startsAt, p_ends_at: endsAt, p_primary: primary, p_backup: backup || null,
  }));
}
export async function removeOnCallShift(id) {
  return asObject(await rpc("inbound_sms_oncall_remove_shift", { p_id: id }));
}
export async function saveOnCallDefaults(primary, backup, ackMinutes) {
  return asObject(await rpc("inbound_sms_oncall_save_defaults", {
    p_primary: primary || null, p_backup: backup || null, p_ack_minutes: Number(ackMinutes) || 15,
  }));
}
export async function ackPage(pageId) {
  return asObject(await rpc("inbound_sms_page_ack", { p_page_id: pageId }));
}
export async function makeQueueTask(conversationId, role) {
  return asObject(await rpc("inbound_sms_queue_make_task", { p_conversation_id: conversationId, p_role: role || null }));
}
export async function fetchOvernightSummary() {
  return asObject(await rpc("inbound_sms_overnight_summary"));
}
